import React from 'react';
import { SEO } from '../components/SEO';
export function TermsPage() {
  return <>
      <SEO title="Aspect — Terms of Use" description="Terms of Use governing access to and use of the Aspect platform and services." data-id="element-736" />
      <div className="space-y-24 md:space-y-32" data-id="element-737">
        {/* Header */}
        <section className="max-w-2xl" data-id="element-738">
          <h1 className="text-3xl md:text-4xl font-medium leading-tight mb-4 text-aspect-black" data-id="element-739">
            Terms of Use
          </h1>
          <p className="text-aspect-gray" data-id="element-740">Last Updated: December 1, 2025</p>
          <div className="mt-8 space-y-4 text-aspect-black/80 leading-relaxed" data-id="element-741">
            <p data-id="element-742">
              These Terms of Use ("Terms") govern access to and use of the
              Aspect website, platform, and related services (collectively, the
              "Service") operated by Aspect ("Aspect," "we," "us," or "our").
            </p>
            <p data-id="element-743">
              By accessing or using the Service, you agree to these Terms. If
              you do not agree, do not use the Service.
            </p>
          </div>
        </section>

        {/* 1. Purpose */}
        <section className="grid md:grid-cols-12 gap-8 md:gap-12 border-t border-aspect-gray/20 pt-12" data-id="element-744">
          <div className="md:col-span-4" data-id="element-745">
            <span className="text-aspect-gray" data-id="element-746">01</span>
          </div>
          <div className="md:col-span-8 space-y-6" data-id="element-747">
            <h2 className="text-2xl font-medium" data-id="element-748">Purpose of the Service</h2>
            <div className="space-y-4 text-aspect-black/80 leading-relaxed max-w-xl" data-id="element-749">
              <p data-id="element-750">
                Aspect provides a rights-cleared, metadata-verified photography
                platform that documents completed architectural projects and
                makes verified imagery available for licensed use. The Service
                functions as infrastructure for rights management, verification,
                and licensing.
              </p>
            </div>
          </div>
        </section>

        {/* 2. Eligibility */}
        <section className="grid md:grid-cols-12 gap-8 md:gap-12 border-t border-aspect-gray/20 pt-12" data-id="element-751">
          <div className="md:col-span-4" data-id="element-752">
            <span className="text-aspect-gray" data-id="element-753">02</span>
          </div>
          <div className="md:col-span-8 space-y-6" data-id="element-754">
            <h2 className="text-2xl font-medium" data-id="element-755">Eligibility</h2>
            <div className="space-y-4 text-aspect-black/80 leading-relaxed max-w-xl" data-id="element-756">
              <p data-id="element-757">You may use the Service only if you:</p>
              <ul className="space-y-2" data-id="element-758">
                <li data-id="element-759">Are at least 18 years old, and</li>
                <li data-id="element-760">
                  Have authority to enter into these Terms on behalf of yourself
                  or an entity you represent.
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* 3. Accounts */}
        <section className="grid md:grid-cols-12 gap-8 md:gap-12 border-t border-aspect-gray/20 pt-12" data-id="element-761">
          <div className="md:col-span-4" data-id="element-762">
            <span className="text-aspect-gray" data-id="element-763">03</span>
          </div>
          <div className="md:col-span-8 space-y-6" data-id="element-764">
            <h2 className="text-2xl font-medium" data-id="element-765">Accounts and Access</h2>
            <div className="space-y-4 text-aspect-black/80 leading-relaxed max-w-xl" data-id="element-766">
              <p data-id="element-767">
                Certain features of the Service require account registration.
              </p>
              <p data-id="element-768">You agree to:</p>
              <ul className="space-y-2" data-id="element-769">
                <li data-id="element-770">Provide accurate and complete information</li>
                <li data-id="element-771">Maintain the confidentiality of your login credentials</li>
                <li data-id="element-772">
                  Promptly notify Aspect of any unauthorized access or security
                  breach
                </li>
              </ul>
              <p data-id="element-773">
                You are responsible for all activity occurring under your
                account.
              </p>
            </div>
          </div>
        </section>

        {/* 4. Roles */}
        <section className="grid md:grid-cols-12 gap-8 md:gap-12 border-t border-aspect-gray/20 pt-12" data-id="element-774">
          <div className="md:col-span-4" data-id="element-775">
            <span className="text-aspect-gray" data-id="element-776">04</span>
          </div>
          <div className="md:col-span-8 space-y-6" data-id="element-777">
            <h2 className="text-2xl font-medium" data-id="element-778">Roles and Participants</h2>
            <div className="space-y-4 text-aspect-black/80 leading-relaxed max-w-xl" data-id="element-779">
              <p data-id="element-780">
                The Service may be used by different participant types,
                including but not limited to:
              </p>
              <ul className="space-y-2" data-id="element-781">
                <li data-id="element-782">Architects</li>
                <li data-id="element-783">Project owners or operators</li>
                <li data-id="element-784">Photographers</li>
                <li data-id="element-785">Building-product manufacturers</li>
              </ul>
              <p data-id="element-786">
                Your rights and obligations may vary depending on your role and
                any additional agreements you enter into with Aspect.
              </p>
            </div>
          </div>
        </section>

        {/* 5. Submissions */}
        <section className="grid md:grid-cols-12 gap-8 md:gap-12 border-t border-aspect-gray/20 pt-12" data-id="element-787">
          <div className="md:col-span-4" data-id="element-788">
            <span className="text-aspect-gray" data-id="element-789">05</span>
          </div>
          <div className="md:col-span-8 space-y-6" data-id="element-790">
            <h2 className="text-2xl font-medium" data-id="element-791">Submissions and Content</h2>
            <div className="space-y-6 text-aspect-black/80 leading-relaxed max-w-xl" data-id="element-792">
              <div data-id="element-793">
                <h3 className="font-medium mb-2" data-id="element-794">5.1 User Submissions</h3>
                <p data-id="element-795">
                  You may submit information, project details, specifications,
                  releases, images, or other materials ("Submissions").
                </p>
                <p className="mt-4" data-id="element-796">
                  By submitting content, you represent that:
                </p>
                <ul className="space-y-2 mt-2" data-id="element-797">
                  <li data-id="element-798">You have the legal right to submit it</li>
                  <li data-id="element-799">
                    The information is accurate to the best of your knowledge
                  </li>
                  <li data-id="element-800">
                    The submission does not violate any third-party rights
                  </li>
                </ul>
                <p className="mt-4" data-id="element-801">
                  Aspect may rely on submitted information for verification,
                  tagging, and licensing workflows.
                </p>
              </div>
              <div data-id="element-802">
                <h3 className="font-medium mb-2" data-id="element-803">
                  5.2 Accuracy and Verification
                </h3>
                <p data-id="element-804">
                  Aspect uses structured workflows, documentation, and review
                  processes to verify products, projects, and rights. However:
                </p>
                <ul className="space-y-2 mt-2" data-id="element-805">
                  <li data-id="element-806">Aspect does not guarantee absolute accuracy</li>
                  <li data-id="element-807">
                    Verification reflects available evidence at the time of
                    review
                  </li>
                  <li data-id="element-808">
                    Unverified or disputed assets may be withheld, corrected, or
                    removed
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* 6. IP */}
        <section className="grid md:grid-cols-12 gap-8 md:gap-12 border-t border-aspect-gray/20 pt-12" data-id="element-809">
          <div className="md:col-span-4" data-id="element-810">
            <span className="text-aspect-gray" data-id="element-811">06</span>
          </div>
          <div className="md:col-span-8 space-y-6" data-id="element-812">
            <h2 className="text-2xl font-medium" data-id="element-813">
              Intellectual Property and Rights
            </h2>
            <div className="space-y-6 text-aspect-black/80 leading-relaxed max-w-xl" data-id="element-814">
              <div data-id="element-815">
                <h3 className="font-medium mb-2" data-id="element-816">6.1 Platform IP</h3>
                <p data-id="element-817">
                  The Service, including software, workflows, schemas,
                  interfaces, and documentation, is owned by Aspect and
                  protected by intellectual property laws. Except as expressly
                  permitted, you may not copy, modify, or reverse engineer any
                  part of the Service.
                </p>
              </div>
              <div data-id="element-818">
                <h3 className="font-medium mb-2" data-id="element-819">
                  6.2 Photography and Media Assets
                </h3>
                <p data-id="element-820">Rights to photographs and related assets are governed by:</p>
                <ul className="space-y-2 mt-2" data-id="element-821">
                  <li data-id="element-822">Photographer agreements</li>
                  <li data-id="element-823">Architect and owner releases</li>
                  <li data-id="element-824">
                    License terms presented at the time of download or use
                  </li>
                </ul>
                <p className="mt-4" data-id="element-825">
                  No rights are granted by default. Usage is permitted only as
                  explicitly licensed.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* 7. Licensing */}
        <section className="grid md:grid-cols-12 gap-8 md:gap-12 border-t border-aspect-gray/20 pt-12" data-id="element-826">
          <div className="md:col-span-4" data-id="element-827">
            <span className="text-aspect-gray" data-id="element-828">07</span>
          </div>
          <div className="md:col-span-8 space-y-6" data-id="element-829">
            <h2 className="text-2xl font-medium" data-id="element-830">
              Licensing and Permitted Use
            </h2>
            <div className="space-y-4 text-aspect-black/80 leading-relaxed max-w-xl" data-id="element-831">
              <p data-id="element-832">When you license an image through the Service:</p>
              <ul className="space-y-2" data-id="element-833">
                <li data-id="element-834">Your rights are limited to the specific license granted</li>
                <li data-id="element-835">Use outside the license scope is prohibited</li>
                <li data-id="element-836">
                  Attribution requirements must be followed where specified
                </li>
              </ul>
              <p data-id="element-837">
                Aspect reserves the right to audit usage and enforce license
                compliance.
              </p>
            </div>
          </div>
        </section>

        {/* 8. Takedowns */}
        <section className="grid md:grid-cols-12 gap-8 md:gap-12 border-t border-aspect-gray/20 pt-12" data-id="element-838">
          <div className="md:col-span-4" data-id="element-839">
            <span className="text-aspect-gray" data-id="element-840">08</span>
          </div>
          <div className="md:col-span-8 space-y-6" data-id="element-841">
            <h2 className="text-2xl font-medium" data-id="element-842">Takedowns and Corrections</h2>
            <div className="space-y-4 text-aspect-black/80 leading-relaxed max-w-xl" data-id="element-843">
              <p data-id="element-844">Aspect may remove, restrict, or modify content if:</p>
              <ul className="space-y-2" data-id="element-845">
                <li data-id="element-846">Rights are unclear or disputed</li>
                <li data-id="element-847">
                  A participant requests removal under an applicable agreement
                </li>
                <li data-id="element-848">Errors or inaccuracies are identified</li>
                <li data-id="element-849">Legal or compliance concerns arise</li>
              </ul>
              <p data-id="element-850">
                Takedown requests must follow Aspect's documented process.
                Removal does not imply fault or liability.
              </p>
            </div>
          </div>
        </section>

        {/* 9. Prohibited Conduct */}
        <section className="grid md:grid-cols-12 gap-8 md:gap-12 border-t border-aspect-gray/20 pt-12" data-id="element-851">
          <div className="md:col-span-4" data-id="element-852">
            <span className="text-aspect-gray" data-id="element-853">09</span>
          </div>
          <div className="md:col-span-8 space-y-6" data-id="element-854">
            <h2 className="text-2xl font-medium" data-id="element-855">Prohibited Conduct</h2>
            <div className="space-y-4 text-aspect-black/80 leading-relaxed max-w-xl" data-id="element-856">
              <p data-id="element-857">You agree not to:</p>
              <ul className="space-y-2" data-id="element-858">
                <li data-id="element-859">Use the Service for unlawful or misleading purposes</li>
                <li data-id="element-860">Circumvent access controls or license restrictions</li>
                <li data-id="element-861">Misrepresent verification status or rights</li>
                <li data-id="element-862">
                  Scrape, bulk download, or redistribute content without
                  authorization
                </li>
                <li data-id="element-863">
                  Interfere with the integrity or operation of the Service
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* 10. Fees */}
        <section className="grid md:grid-cols-12 gap-8 md:gap-12 border-t border-aspect-gray/20 pt-12" data-id="element-864">
          <div className="md:col-span-4" data-id="element-865">
            <span className="text-aspect-gray" data-id="element-866">10</span>
          </div>
          <div className="md:col-span-8 space-y-6" data-id="element-867">
            <h2 className="text-2xl font-medium" data-id="element-868">Fees and Payments</h2>
            <div className="space-y-4 text-aspect-black/80 leading-relaxed max-w-xl" data-id="element-869">
              <p data-id="element-870">
                Certain features, licenses, or subscriptions require payment.
              </p>
              <p data-id="element-871">All fees:</p>
              <ul className="space-y-2" data-id="element-872">
                <li data-id="element-873">Are disclosed before purchase</li>
                <li data-id="element-874">Are non-refundable unless expressly stated otherwise</li>
                <li data-id="element-875">May be modified with notice for future periods</li>
              </ul>
              <p data-id="element-876">Taxes and third-party charges are your responsibility.</p>
            </div>
          </div>
        </section>

        {/* 11. Disclaimers */}
        <section className="grid md:grid-cols-12 gap-8 md:gap-12 border-t border-aspect-gray/20 pt-12" data-id="element-877">
          <div className="md:col-span-4" data-id="element-878">
            <span className="text-aspect-gray" data-id="element-879">11</span>
          </div>
          <div className="md:col-span-8 space-y-6" data-id="element-880">
            <h2 className="text-2xl font-medium" data-id="element-881">Disclaimers</h2>
            <div className="space-y-4 text-aspect-black/80 leading-relaxed max-w-xl" data-id="element-882">
              <p className="uppercase" data-id="element-883">
                The Service is provided "as is" and "as available."
              </p>
              <p data-id="element-884">
                Aspect disclaims all warranties except where prohibited by law,
                including implied warranties of merchantability, fitness for a
                particular purpose, and non-infringement.
              </p>
              <p data-id="element-885">Aspect does not warrant that:</p>
              <ul className="space-y-2" data-id="element-886">
                <li data-id="element-887">The Service will be uninterrupted or error-free</li>
                <li data-id="element-888">
                  All metadata or verification outcomes are complete or
                  permanent
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* 12. Limitation */}
        <section className="grid md:grid-cols-12 gap-8 md:gap-12 border-t border-aspect-gray/20 pt-12" data-id="element-889">
          <div className="md:col-span-4" data-id="element-890">
            <span className="text-aspect-gray" data-id="element-891">12</span>
          </div>
          <div className="md:col-span-8 space-y-6" data-id="element-892">
            <h2 className="text-2xl font-medium" data-id="element-893">Limitation of Liability</h2>
            <div className="space-y-4 text-aspect-black/80 leading-relaxed max-w-xl" data-id="element-894">
              <p data-id="element-895">To the maximum extent permitted by law:</p>
              <ul className="space-y-2" data-id="element-896">
                <li data-id="element-897">
                  Aspect shall not be liable for indirect, incidental, special,
                  or consequential damages
                </li>
                <li data-id="element-898">
                  Aspect's total liability shall not exceed the amount paid by
                  you to Aspect in the twelve (12) months preceding the claim
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* 13. Indemnification */}
        <section className="grid md:grid-cols-12 gap-8 md:gap-12 border-t border-aspect-gray/20 pt-12" data-id="element-899">
          <div className="md:col-span-4" data-id="element-900">
            <span className="text-aspect-gray" data-id="element-901">13</span>
          </div>
          <div className="md:col-span-8 space-y-6" data-id="element-902">
            <h2 className="text-2xl font-medium" data-id="element-903">Indemnification</h2>
            <div className="space-y-4 text-aspect-black/80 leading-relaxed max-w-xl" data-id="element-904">
              <p data-id="element-905">
                You agree to indemnify and hold harmless Aspect from claims
                arising out of:
              </p>
              <ul className="space-y-2" data-id="element-906">
                <li data-id="element-907">Your submissions</li>
                <li data-id="element-908">Your misuse of the Service</li>
                <li data-id="element-909">Your violation of these Terms or applicable law</li>
              </ul>
            </div>
          </div>
        </section>

        {/* 14. Termination */}
        <section className="grid md:grid-cols-12 gap-8 md:gap-12 border-t border-aspect-gray/20 pt-12" data-id="element-910">
          <div className="md:col-span-4" data-id="element-911">
            <span className="text-aspect-gray" data-id="element-912">14</span>
          </div>
          <div className="md:col-span-8 space-y-6" data-id="element-913">
            <h2 className="text-2xl font-medium" data-id="element-914">Termination</h2>
            <div className="space-y-4 text-aspect-black/80 leading-relaxed max-w-xl" data-id="element-915">
              <p data-id="element-916">Aspect may suspend or terminate access:</p>
              <ul className="space-y-2" data-id="element-917">
                <li data-id="element-918">For violation of these Terms</li>
                <li data-id="element-919">To protect rights, integrity, or legal compliance</li>
                <li data-id="element-920">At its discretion with or without notice</li>
              </ul>
              <p data-id="element-921">
                Termination does not affect accrued obligations or licensed
                rights already granted.
              </p>
            </div>
          </div>
        </section>

        {/* 15. Changes */}
        <section className="grid md:grid-cols-12 gap-8 md:gap-12 border-t border-aspect-gray/20 pt-12" data-id="element-922">
          <div className="md:col-span-4" data-id="element-923">
            <span className="text-aspect-gray" data-id="element-924">15</span>
          </div>
          <div className="md:col-span-8 space-y-6" data-id="element-925">
            <h2 className="text-2xl font-medium" data-id="element-926">Changes to the Terms</h2>
            <div className="space-y-4 text-aspect-black/80 leading-relaxed max-w-xl" data-id="element-927">
              <p data-id="element-928">
                Aspect may update these Terms periodically. Continued use of the
                Service after changes constitutes acceptance of the updated
                Terms.
              </p>
            </div>
          </div>
        </section>

        {/* 16. Governing Law */}
        <section className="grid md:grid-cols-12 gap-8 md:gap-12 border-t border-aspect-gray/20 pt-12" data-id="element-929">
          <div className="md:col-span-4" data-id="element-930">
            <span className="text-aspect-gray" data-id="element-931">16</span>
          </div>
          <div className="md:col-span-8 space-y-6" data-id="element-932">
            <h2 className="text-2xl font-medium" data-id="element-933">Governing Law</h2>
            <div className="space-y-4 text-aspect-black/80 leading-relaxed max-w-xl" data-id="element-934">
              <p data-id="element-935">
                These Terms are governed by the laws of the State of New York,
                without regard to conflict-of-laws principles.
              </p>
            </div>
          </div>
        </section>

        {/* 17. Contact */}
        <section className="grid md:grid-cols-12 gap-8 md:gap-12 border-t border-aspect-gray/20 pt-12 pb-12" data-id="element-936">
          <div className="md:col-span-4" data-id="element-937">
            <span className="text-aspect-gray" data-id="element-938">17</span>
          </div>
          <div className="md:col-span-8 space-y-6" data-id="element-939">
            <h2 className="text-2xl font-medium" data-id="element-940">Contact</h2>
            <div className="space-y-4 text-aspect-black/80 leading-relaxed max-w-xl" data-id="element-941">
              <p data-id="element-942">Questions regarding these Terms may be directed to:</p>
              <p className="font-medium" data-id="element-943">legal@aspectstudio.io</p>
            </div>
          </div>
        </section>
      </div>
    </>;
}